import { parseDRFErrors } from "./drfErrors";

export { parseDRFErrors };

export function resolveExcelBreedingNumber(choices, value) {
  if (value != null) {
    const number = Number(value);
    if (!Number.isInteger(number) || number <= 0 || (choices.length && !choices.includes(number))) {
      throw new Error("사육번호는 해당 농장의 번호로 입력해야 합니다.");
    }
    return number;
  }
  if (choices.length > 1) {
    throw new Error("사육번호가 여러 개인 농장은 엑셀에 실제 사육번호를 입력해야 합니다.");
  }
  return choices[0] ?? null;
}

export function getProductCustomerIds(product) {
  const customers = Array.isArray(product?.customers) ? product.customers
    : product?.customers === undefined && product?.customer != null ? [product.customer] : [];
  return [...new Set(customers.map((customer) => String(customer?.id ?? customer ?? "").trim()).filter(Boolean))];
}

export function getCommonProductFarmIds(product, customersById) {
  const customerIds = getProductCustomerIds(product);
  if (!customerIds.length) return [];
  const farmLists = customerIds.map((id) => (customersById[id]?.available_farms || [])
    .map((farm) => String(farm?.id ?? farm)));
  return [...new Set(farmLists[0])].filter((id) => farmLists.every((farms) => farms.includes(id)));
}

export function getCustomerProductOptions(products, customerId, existingRecord) {
  const selectedCustomer = String(customerId || "");
  if (!selectedCustomer) return [];
  const options = (products || []).filter((product) => getProductCustomerIds(product).includes(selectedCustomer));
  const originalCustomer = String(existingRecord?.customer?.id ?? existingRecord?.customer ?? "");
  const originalProduct = String(existingRecord?.product?.id ?? existingRecord?.product ?? "");
  if (originalCustomer === selectedCustomer && originalProduct && !options.some((product) => String(product.id) === originalProduct)) {
    options.push((products || []).find((product) => String(product.id) === originalProduct) || {
      id: originalProduct,
      product_name: existingRecord.product_name || originalProduct,
      product_no: existingRecord.product_no || "",
    });
  }
  return options;
}

// Consolidated utilities (single entry point)
// Goal: reduce duplicate helper files while keeping existing behavior.

// --- API list response normalizer ---
// Supports plain array responses and DRF-style { results: [...] }
export function unwrapList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.results)) return data.results;
  return [];
}

export function normalizePaginatedData(data) {
  if (Array.isArray(data)) {
    return {
      count: data.length,
      next: null,
      previous: null,
      results: data,
    };
  }

  if (data && typeof data === "object") {
    const { count, next, previous, results, ...extra } = data;
    return {
      ...extra,
      count: Number(count) || 0,
      next: typeof next === "string" ? next : null,
      previous: typeof previous === "string" ? previous : null,
      results: Array.isArray(results) ? results : [],
    };
  }

  return {
    count: 0,
    next: null,
    previous: null,
    results: [],
  };
}

function decodeHtmlEntities(text) {
  return String(text || "")
    .replace(/&#x27;/g, "'")
    .replace(/&quot;/g, "\"")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function extractHtmlErrorMessage(html, fallback) {
  const source = String(html || "");
  if (!source) return fallback;

  const exceptionValueMatch = source.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/i);
  if (exceptionValueMatch?.[1]) {
    const message = decodeHtmlEntities(exceptionValueMatch[1]).replace(/<[^>]+>/g, "").trim();
    if (message) return message;
  }

  const titleMatch = source.match(/<title>([\s\S]*?)<\/title>/i);
  if (titleMatch?.[1]) {
    const message = decodeHtmlEntities(titleMatch[1]).replace(/\s+/g, " ").trim();
    if (message) return message;
  }

  return fallback;
}

// --- Axios error helpers ---
// Extracts the most relevant message from server response (DRF-friendly)
export function getApiErrorMessage(err, fallback = "요청에 실패했습니다.") {
  const data = err?.response?.data;
  if (!data) {
    const message = String(err?.message || "").trim();
    if (message && !/^Request failed with status code \d+$/i.test(message)) return message;
    return fallback;
  }

  if (typeof data === "string") {
    if (/^\s*(?:<!DOCTYPE html>|<html\b)/i.test(data)) {
      return extractHtmlErrorMessage(data, fallback);
    }
    return data;
  }

  const parsed = parseDRFErrors(data);
  if (parsed?.form) return parsed.form;

  // Field-only errors: join values
  if (parsed?.fields && Object.keys(parsed.fields).length > 0) {
    return Object.values(parsed.fields).filter(Boolean).join("\n");
  }

  // Non-standard keys
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.detail === "string" && data.detail) return data.detail;
  if (typeof data?.error === "string" && data.error) return data.error;
  if (typeof data?.errors === "string" && data.errors) return data.errors;

  if (Array.isArray(data)) return data.filter(Boolean).join("\n");

  if (typeof data === "object") {
    const flat = [];
    Object.values(data).forEach((v) => {
      if (!v) return;
      if (typeof v === "string") flat.push(v);
      else if (Array.isArray(v)) flat.push(v.filter(Boolean).join("\n"));
    });
    if (flat.length) return flat.join("\n");
  }

  return fallback;
}

// Field-level error map (DRF validation)
export function getApiFieldErrors(err) {
  const data = err?.response?.data;
  const parsed = parseDRFErrors(data);
  return parsed?.fields || {};
}

// --- Table helpers ---
export const BOOL_OPTIONS = [
  { value: "", label: "전체" },
  { value: "true", label: "예" },
  { value: "false", label: "아니오" },
];

export function asText(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

export function formatShortYmd(value) {
  const raw = asText(value).trim();
  if (!raw) return "";
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return raw;
  return `${match[1].slice(2)}.${match[2]}.${match[3]}`;
}

export function formatShortDateTime(value) {
  const raw = asText(value).trim();
  if (!raw) return "";

  const fullMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
  if (fullMatch) {
    return `${fullMatch[1].slice(2)}.${fullMatch[2]}.${fullMatch[3]} ${fullMatch[4]}`;
  }

  const minuteMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}:\d{2})/);
  if (minuteMatch) {
    return `${minuteMatch[1].slice(2)}.${minuteMatch[2]}.${minuteMatch[3]} ${minuteMatch[4]}`;
  }

  return formatShortYmd(raw);
}

export function formatQuantity(value, fallback = "") {
  const raw = asText(value).trim();
  if (!raw) return fallback;

  const numericValue = Number(raw);
  if (!Number.isFinite(numericValue)) return raw;

  return numericValue.toLocaleString("ko-KR", {
    maximumFractionDigits: Number.isInteger(numericValue) ? 0 : 2,
  });
}

export function formatQuantityFixed2(value, fallback = "0.00") {
  const raw = asText(value).trim();
  if (!raw) return fallback;

  const normalized = raw.replace(/,/g, "");
  const match = normalized.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return raw;

  const sign = match[1] || "";
  const integerPart = match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const decimalPart = (match[3] || "").padEnd(2, "0").slice(0, 2);
  return `${sign}${integerPart}.${decimalPart}`;
}

export function includesText(hay, needle) {
  return asText(hay).toLowerCase().includes(asText(needle).toLowerCase().trim());
}

export function matchBool(value, filterValue) {
  if (filterValue === "") return true;
  const want = filterValue === "true";
  return Boolean(value) === want;
}

export function includesAllTokens(hay, query) {
  const tokens = asText(query)
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const h = asText(hay).toLowerCase();
  return tokens.every((t) => h.includes(t));
}

// Like includesAllTokens, but matches if ANY token exists (OR)
export function includesAnyTokens(hay, query) {
  const tokens = asText(query)
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const h = asText(hay).toLowerCase();
  return tokens.some((t) => h.includes(t));
}

export function getFarmBuildingCount(value) {
  const count = Number(value);
  if (!Number.isInteger(count) || count < 1) return null;
  return count;
}

export function formatFarmDisplayLabel(farm, options = {}) {
  const { includeFarmId = true, includeBuildingCount = true } = options;

  if (farm === null || farm === undefined) return "-";
  if (typeof farm !== "object") return asText(farm).trim() || "-";

  const name = asText(farm?.farm_name ?? farm?.name ?? farm?.title).trim();
  const farmId = asText(farm?.farm_id).trim();
  const pk = asText(farm?.id ?? farm?.pk).trim();
  const buildingCount = getFarmBuildingCount(farm?.building_count);
  const base = name || farmId || pk || "-";
  const extras = [];

  if (includeFarmId && farmId && farmId !== base) extras.push(farmId);
  if (includeBuildingCount && buildingCount !== null) extras.push(`${buildingCount}동`);

  if (!extras.length) return base;
  return `${base} (${extras.join(" / ")})`;
}

function normalizeFarmLookupKey(value) {
  return asText(value).trim().toLowerCase();
}

function pushFarmLookup(map, key, farm) {
  const normalizedKey = normalizeFarmLookupKey(key);
  if (!normalizedKey) return;
  if (!map[normalizedKey]) map[normalizedKey] = [];
  map[normalizedKey].push(farm);
}

function parseFarmReferenceToken(value) {
  const raw = asText(value).trim();
  if (!raw) return { token: "", buildingCount: null };

  const match = raw.match(/^(.*?)(?:\s*[-/]\s*|\s+|\s*\(\s*|\s*\[\s*)(\d+)\s*동(?:\s*\)|\s*\])?\s*$/);
  if (!match) return { token: raw, buildingCount: null };

  const token = asText(match[1]).replace(/[-/]+$/, "").trim();
  const buildingCount = getFarmBuildingCount(match[2]);
  if (!token || buildingCount === null) return { token: raw, buildingCount: null };

  return { token, buildingCount };
}

export function createFarmReferenceResolver(farms = []) {
  const byPk = {};
  const byFarmId = {};
  const byName = {};

  (farms || []).forEach((farm) => {
    pushFarmLookup(byPk, farm?.id, farm);
    pushFarmLookup(byPk, farm?.pk, farm);
    pushFarmLookup(byFarmId, farm?.farm_id, farm);
    pushFarmLookup(byName, farm?.farm_name, farm);
  });

  return (value, options = {}) => {
    const parsed = parseFarmReferenceToken(value);
    const token = parsed.token;
    const explicitBuildingCount = getFarmBuildingCount(options?.buildingCount);
    const buildingCount = explicitBuildingCount ?? parsed.buildingCount;

    if (!token) {
      return {
        farm: null,
        matches: [],
        token: "",
        buildingCount,
        status: "empty",
        error: null,
      };
    }

    const pkMatches = byPk[normalizeFarmLookupKey(token)] || [];
    if (pkMatches.length === 1) {
      return {
        farm: pkMatches[0],
        matches: pkMatches,
        token,
        buildingCount,
        status: "matched",
        error: null,
      };
    }

    const farmIdMatches = byFarmId[normalizeFarmLookupKey(token)] || [];
    const nameMatches = byName[normalizeFarmLookupKey(token)] || [];
    const baseMatches = farmIdMatches.length ? farmIdMatches : nameMatches;

    if (!baseMatches.length) {
      return {
        farm: null,
        matches: [],
        token,
        buildingCount,
        status: "not_found",
        error: null,
      };
    }

    const filteredMatches = buildingCount === null
      ? baseMatches
      : baseMatches.filter((farm) => getFarmBuildingCount(farm?.building_count) === buildingCount);

    if (filteredMatches.length === 1) {
      return {
        farm: filteredMatches[0],
        matches: filteredMatches,
        token,
        buildingCount,
        status: "matched",
        error: null,
      };
    }

    if (!filteredMatches.length && buildingCount !== null) {
      return {
        farm: null,
        matches: baseMatches,
        token,
        buildingCount,
        status: "building_not_found",
        error: `${token} ${buildingCount}동에 해당하는 농장을 찾을 수 없습니다.`,
      };
    }

    return {
      farm: null,
      matches: filteredMatches.length ? filteredMatches : baseMatches,
      token,
      buildingCount,
      status: "ambiguous",
      error: `동일한 농장이 여러 개입니다: ${token}. 농장 ID 또는 동수를 함께 입력해주세요.`,
    };
  };
}

export function farmsToText(farms, farmsById) {
  if (!farms) return "";
  const byId = farmsById || null;

  const toLabel = (f) => {
    if (f === null || f === undefined) return "";
    // object form: {id, farm_name, ...}
    if (typeof f === "object") {
      return formatFarmDisplayLabel(f);
    }

    // primitive id or name
    const id = asText(f).trim();
    if (byId && id && byId[id]) {
      return formatFarmDisplayLabel(byId[id]);
    }
    return id;
  };

  if (Array.isArray(farms)) {
    return farms.map(toLabel).filter(Boolean).join(", ");
  }
  return toLabel(farms);
}

export function getFirstValue(obj, keys = [], fallback = "") {
  if (!obj || typeof obj !== "object") return fallback;
  for (const key of keys) {
    if (!key) continue;
    const v = obj[key];
    if (v !== null && v !== undefined && String(v).trim() !== "") {
      return v;
    }
  }
  return fallback;
}

export function formatDisplayNo(prefix, value, fallback = "") {
  const normalizedPrefix = asText(prefix).trim();
  const raw = asText(value).trim();
  if (!normalizedPrefix || !raw) return fallback;
  if (raw.toUpperCase().startsWith(`${normalizedPrefix.toUpperCase()}-`)) return raw;
  return `${normalizedPrefix}-${raw}`;
}

export function getDisplayNo(obj, field, prefix, idKeys = ["id"], fallback = "") {
  const explicit = asText(obj?.[field]).trim();
  if (explicit) return explicit;
  const idValue = getFirstValue(obj, idKeys, "");
  return formatDisplayNo(prefix, idValue, fallback);
}

export function parseDisplayNoNumber(value, prefix) {
  const raw = asText(value).trim();
  if (!raw) return "";
  const normalizedPrefix = asText(prefix).trim();
  const pattern = normalizedPrefix
    ? new RegExp(`^${normalizedPrefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}-\\s*(\\d+)$`, "i")
    : null;
  const prefixedMatch = pattern ? raw.match(pattern) : null;
  if (prefixedMatch) return prefixedMatch[1];
  return /^\d+$/.test(raw) ? raw : "";
}

export function getIdentifierLabel(obj, keys = [], fallbackKeys = ["id"]) {
  const value = getFirstValue(obj, [...keys, ...fallbackKeys], "");
  return asText(value);
}

export function normalizeFarmType(value) {
  const v = asText(value).trim();
  if (!v) return "";
  if (v === "계약사육농장") return "동물복지농장";
  if (v === "수입농장" || v === "수입란") return "수입";
  return v;
}

export function toStringArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((v) => asText(v).trim())
      .filter(Boolean);
  }

  const raw = asText(value).trim();
  if (!raw) return [];
  return raw
    .split(/\r?\n|,/)
    .map((v) => asText(v).trim())
    .filter(Boolean);
}
